import { useEffect } from 'react';

/**
 * Hook to detect service worker updates and automatically reload the page.
 * This ensures users always get the latest version after a publication.
 */
export const useServiceWorkerUpdate = () => {
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;

    const handleUpdate = (registration: ServiceWorkerRegistration) => {
      if (registration.waiting) {
        // Send signal to activate new SW immediately
        registration.waiting.postMessage('SKIP_WAITING');
      }
    };

    navigator.serviceWorker.ready.then((registration) => {
      // Check for updates every 30 minutes
      const intervalId = setInterval(() => {
        registration.update();
      }, 30 * 60 * 1000);

      // Also check on page visibility change (when user returns to app)
      const handleVisibilityChange = () => {
        if (document.visibilityState === 'visible') {
          registration.update();
        }
      };
      document.addEventListener('visibilitychange', handleVisibilityChange);

      // Listen for new service worker installation
      registration.addEventListener('updatefound', () => {
        const newWorker = registration.installing;
        if (!newWorker) return;

        newWorker.addEventListener('statechange', () => {
          // New SW installed and ready, trigger update
          if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
            handleUpdate(registration);
          }
        });
      });

      // Cleanup on unmount
      return () => {
        clearInterval(intervalId);
        document.removeEventListener('visibilitychange', handleVisibilityChange);
      };
    });

    // When controller changes (new SW active), reload the page
    let refreshing = false;
    const handleControllerChange = () => {
      if (!refreshing) {
        refreshing = true;
        window.location.reload();
      }
    };
    navigator.serviceWorker.addEventListener('controllerchange', handleControllerChange);

    return () => {
      navigator.serviceWorker.removeEventListener('controllerchange', handleControllerChange);
    };
  }, []);
};
